import { useNavigate } from "react-router-dom";
import { CheckCircle, Star, ArrowRight } from "@phosphor-icons/react";

const plans = [
  {
    name: "Starter",
    price: "$3",
    period: "per user / month",
    description: "For small teams getting started with shared task management.",
    features: [
      "Up to 20 team members",
      "Boards, tasks, and projects",
      "7-day task history",
      "Email support",
    ],
    cta: "Start Free Trial",
    featured: false,
  },
  {
    name: "Business",
    price: "$8",
    period: "per user / month",
    description: "For growing teams that need richer collaboration and analytics.",
    features: [
      "Unlimited team members",
      "Atlas AI assistant",
      "Team analytics dashboard",
      "Capacity planning",
      "Priority support",
    ],
    cta: "Start Free Trial",
    featured: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "tailored pricing",
    description: "For large organizations with complex requirements.",
    features: [
      "Everything in Business",
      "SSO & SCIM provisioning",
      "Advanced security controls",
      "Dedicated success manager",
      "SLA guarantees",
      "On-premise deployment option",
    ],
    cta: "Contact Sales",
    featured: false,
  },
];

export function PricingSection() {
  const navigate = useNavigate();
  return (
    <section
      id="pricing"
      className="relative bg-white dark:bg-slate-950 py-24 lg:py-32"
    >
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="mx-auto max-w-2xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary-500/20 dark:border-primary-500/30 bg-primary-500/5 dark:bg-primary-500/10 px-4 py-1.5 text-xs font-semibold tracking-wide text-primary-500 dark:text-primary-400 uppercase">
            <Star size={14} weight="fill" />
            Pricing
          </div>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl lg:text-5xl">
            Plans that <span className="gradient-text">scale with you</span>
          </h2>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">
            Start free for 1 month. No credit card required. Upgrade as your
            team grows.
          </p>
          <p className="mt-3 text-sm font-medium text-slate-500 dark:text-slate-400">
            Atlas AI usage is metered and billed separately on Business and Enterprise plans.
          </p>
        </div>

        <div className="mt-16 grid gap-8 lg:grid-cols-3">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`card-snap relative rounded-2xl border p-8 ${
                plan.featured
                  ? "border-primary-500/50 bg-gradient-to-b from-primary-500/5 to-white dark:from-primary-500/10 dark:to-slate-900 shadow-xl shadow-primary-500/10"
                  : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
              }`}
            >
              {plan.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-primary-500 to-primary-400 px-4 py-1 text-xs font-semibold text-white shadow-lg shadow-primary-500/25">
                  Most Popular
                </div>
              )}

              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                {plan.name}
              </h3>

              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
                  {plan.price}
                </span>
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  {plan.period}
                </span>
              </div>

              <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
                {plan.description}
              </p>

              <button
                onClick={() =>
                  plan.cta === "Start Free Trial"
                    ? navigate("/signup")
                    : navigate("/contact")
                }
                className={`mt-6 flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold transition-all duration-200 ${
                  plan.featured
                    ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 hover:brightness-110"
                    : "border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-primary-500 hover:text-primary-500 dark:hover:border-primary-400 dark:hover:text-primary-400"
                }`}
              >
                {plan.cta}
                <ArrowRight size={14} weight="bold" />
              </button>

              <div className="mt-8 space-y-3">
                {plan.features.map((feature) => (
                  <div key={feature} className="flex items-center gap-3">
                    <CheckCircle
                      size={18}
                      weight="fill"
                      className={
                        plan.featured
                          ? "text-primary-500 shrink-0"
                          : "text-slate-400 dark:text-slate-500 shrink-0"
                      }
                    />
                    <span className="text-sm text-slate-600 dark:text-slate-300">
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
